import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

interface InvalidPlotIDsDialogProps {
  open: boolean;
  errors: Array<{ row: number; plotID: string; reason: string }>;
  validCount: number;
  totalCount: number;
  onContinue: () => void;
  onCancel: () => void;
}

export const InvalidPlotIDsDialog: React.FC<InvalidPlotIDsDialogProps> = ({
  open,
  errors,
  validCount,
  totalCount,
  onContinue,
  onCancel,
}) => {
  const invalidCount = errors.length;
  const maxErrorsToShow = 10;
  const errorsToDisplay = errors.slice(0, maxErrorsToShow);
  const hasMoreErrors = errors.length > maxErrorsToShow;
  const allInvalid = validCount === 0;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {allInvalid ? 'All Plot IDs Are Invalid' : 'Invalid Plot IDs Detected'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-4">
            <p>
              Found <strong>{invalidCount}</strong> invalid plot ID{invalidCount !== 1 ? 's' : ''} out of {totalCount} total rows.
              These plot IDs contain characters that are not allowed in Firestore document IDs.
            </p>

            <div className="bg-muted p-3 rounded-md max-h-60 overflow-y-auto">
              <p className="font-semibold text-sm mb-2 text-foreground">Invalid Plot IDs:</p>
              <ul className="space-y-1 text-sm">
                {errorsToDisplay.map((error, index) => (
                  <li key={index} className="font-mono text-xs">
                    <span className="text-muted-foreground">Row {error.row}:</span>{' '}
                    <span className="text-destructive font-semibold">"{error.plotID}"</span>{' '}
                    <span className="text-muted-foreground">({error.reason})</span>
                  </li>
                ))}
                {hasMoreErrors && (
                  <li className="text-muted-foreground italic text-xs pt-1">
                    ... and {errors.length - maxErrorsToShow} more
                  </li>
                )}
              </ul>
            </div>

            {allInvalid ? (
              <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
                <p className="text-sm text-foreground">
                  <strong>All plot IDs in this CSV are invalid.</strong> You must fix the CSV before continuing.
                  No plots can be created with the current data.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-md border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-foreground">
                  <strong>If you continue:</strong>
                </p>
                <ul className="list-disc list-inside text-sm mt-1 space-y-1 text-foreground">
                  <li>
                    <strong>{validCount}</strong> valid plot{validCount !== 1 ? 's' : ''} will be created
                  </li>
                  <li>
                    <strong>{invalidCount}</strong> invalid plot{invalidCount !== 1 ? 's' : ''} will be skipped
                  </li>
                  <li>You can fix and re-upload the CSV later to add the skipped plots</li>
                </ul>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              <strong>Tip:</strong> Plot IDs cannot contain "/" (forward slash), cannot be "." or "..",
              and must be under 1,500 characters.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {allInvalid ? 'Close (Fix CSV)' : 'Cancel (Fix CSV)'}
          </AlertDialogCancel>
          {!allInvalid && (
            <AlertDialogAction onClick={onContinue} className="bg-primary">
              Continue (Create {validCount} Plot{validCount !== 1 ? 's' : ''})
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
